import { useQuery } from '@tanstack/react-query';
import type {
  BalanceSheetResponse,
  CashFlowResponse,
  DrilldownResponse,
  GeneralLedgerResponse,
  IncomeStatementResponse,
  Nec1099Report,
  TrialBalanceResponse,
  WeeklyProfitabilityResponse,
} from '@accounting/shared';
import { api } from '../lib/api';
import { CONSOLIDATED } from '../stores/useCompanyStore';

/** Report path for a single company or the consolidated view. */
function reportPath(selection: string, suffix: string): string {
  return selection === CONSOLIDATED
    ? `/consolidated/reports/${suffix}`
    : `/companies/${selection}/reports/${suffix}`;
}

export function useBalanceSheet(selection: string | null, asOf: string, activeOnly = false) {
  return useQuery({
    queryKey: ['report', 'balance-sheet', selection ?? 'none', asOf, activeOnly],
    enabled: !!selection && !!asOf,
    queryFn: () =>
      api.get<BalanceSheetResponse>(
        reportPath(selection!, 'balance-sheet'),
        activeOnly ? { asOf, activeOnly: 'true' } : { asOf },
      ),
  });
}

export function useTrialBalance(selection: string | null, asOf: string, activeOnly = false) {
  return useQuery({
    queryKey: ['report', 'trial-balance', selection ?? 'none', asOf, activeOnly],
    enabled: !!selection && !!asOf,
    queryFn: () =>
      api.get<TrialBalanceResponse>(
        reportPath(selection!, 'trial-balance'),
        activeOnly ? { asOf, activeOnly: 'true' } : { asOf },
      ),
  });
}

export function useIncomeStatement(
  selection: string | null,
  from: string,
  to: string,
  activeOnly = false,
) {
  return useQuery({
    queryKey: ['report', 'income-statement', selection ?? 'none', from, to, activeOnly],
    enabled: !!selection && !!from && !!to,
    queryFn: () =>
      api.get<IncomeStatementResponse>(
        reportPath(selection!, 'income-statement'),
        activeOnly ? { from, to, activeOnly: 'true' } : { from, to },
      ),
  });
}

export function useCashFlow(selection: string | null, from: string, to: string) {
  return useQuery({
    queryKey: ['report', 'cash-flow', selection ?? 'none', from, to],
    enabled: !!selection && !!from && !!to,
    queryFn: () =>
      api.get<CashFlowResponse>(reportPath(selection!, 'cash-flow'), { from, to }),
  });
}

export function useWeeklyProfitability(selection: string | null, from: string, to: string) {
  return useQuery({
    queryKey: ['report', 'weekly-profitability', selection ?? 'none', from, to],
    enabled: !!selection && !!from && !!to,
    queryFn: () =>
      api.get<WeeklyProfitabilityResponse>(reportPath(selection!, 'weekly-profitability'), { from, to }),
  });
}

/** The transactions behind a report figure (one or more account codes). */
export function useDrilldown(
  selection: string | null,
  params: { codes: string[]; from?: string; to?: string } | null,
) {
  return useQuery({
    queryKey: [
      'report',
      'drilldown',
      selection ?? 'none',
      params?.codes.join(',') ?? '',
      params?.from ?? '',
      params?.to ?? '',
    ],
    enabled: !!selection && !!params && params.codes.length > 0,
    queryFn: () => {
      const q: Record<string, string> = { codes: params!.codes.join(',') };
      if (params!.from) q.from = params!.from;
      if (params!.to) q.to = params!.to;
      const path =
        selection === CONSOLIDATED
          ? '/consolidated/reports/drilldown'
          : `/companies/${selection}/reports/drilldown`;
      return api.get<DrilldownResponse>(path, q);
    },
  });
}

/** 1099-NEC report (per-company only). */
export function useNec1099(companyId: string | null, year: number) {
  return useQuery({
    queryKey: ['report', '1099-nec', companyId ?? 'none', year],
    enabled: companyId !== null && companyId !== CONSOLIDATED,
    queryFn: () =>
      api.get<Nec1099Report>(`/companies/${companyId}/reports/1099-nec`, {
        year: String(year),
      }),
  });
}

/** General Ledger is per-company only (no consolidated variant). */
export function useGeneralLedger(
  companyId: string | null,
  params: { from: string; to: string; accountId?: string },
) {
  return useQuery({
    queryKey: [
      'report',
      'general-ledger',
      companyId ?? 'none',
      params.from,
      params.to,
      params.accountId ?? 'all',
    ],
    enabled: companyId !== null && companyId !== CONSOLIDATED && !!params.from && !!params.to,
    queryFn: () => {
      const q: Record<string, string> = { from: params.from, to: params.to };
      if (params.accountId) q.accountId = params.accountId;
      return api.get<GeneralLedgerResponse>(
        `/companies/${companyId}/reports/general-ledger`,
        q,
      );
    },
  });
}
